import { Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import {
  hasComposerContent,
  useDocumentChatComposer,
} from './document-chat-composer-context';
import { DocumentComposerModelPicker } from './document-composer-model-picker';
import { DocumentThreadPicker } from './document-thread-picker';
import { useEditDocument } from './edit-document-context';
import { stringifyEditorJs } from './editorjs-types';
import { usePromptSubmit } from './prompt-command';

import { ChatComposerRow } from '#src/components/chat-composer-row';
import { MessageList } from '#src/components/message-list';
import { useDocumentScopedMessages } from '#src/hooks/use-document-scoped-messages';
import { useScrollMessageListToEnd } from '#src/hooks/use-scroll-message-list-to-end';
import { usePageFooter } from '#src/providers/page-layout-provider';
import { useCurrentUser } from '#src/providers/user-provider';

export interface DocumentChatFooterProps {
  documentId: string | undefined;
  isNewDocument: boolean;
  /** When false, the footer is not mounted in the shell and layout uses float mode. */
  active?: boolean;
}

/**
 * Document footer: when a thread is focused, show that thread’s messages above the composer;
 * otherwise only the thread picker lists threads (full history in the messages drawer). Submit
 * runs the document AI edit processor. Portaled into [`PageFooter`](../../components/page-footer.tsx).
 */
export function DocumentChatFooter({
  documentId,
  isNewDocument,
  active = true,
}: DocumentChatFooterProps) {
  const { footerRef } = usePageFooter();
  const user = useCurrentUser();
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!active) {
      setPortalTarget(null);
      return () => {
        setPortalTarget(null);
      };
    }

    let cancelled = false;
    const tryAttach = (attempt: number) => {
      if (cancelled) return;
      if (footerRef.current) {
        setPortalTarget(footerRef.current);
        return;
      }
      if (attempt > 50) return;
      setTimeout(() => tryAttach(attempt + 1), 0);
    };
    tryAttach(0);

    return () => {
      cancelled = true;
      setPortalTarget(null);
    };
  }, [active, footerRef]);

  const {
    sortedMessages,
    displayMessages,
    focusedRootId,
    isMessagesInitialLoad,
    messagesError,
    refetchMessages,
    handleRunChatMessageQuery,
    chatMessageQueryLoadingId,
    expandThread,
    collapseThread,
    replaceStripFocusParam,
    messagesFeatureActive,
  } = useDocumentScopedMessages(documentId, {
    enabled: active && Boolean(documentId),
  });

  /** Continue the focused thread, or else the newest document thread so each edit appends to the log. */
  const messageLogThreadRootId = useMemo(() => {
    if (focusedRootId) return focusedRootId;
    if (sortedMessages.length === 0) return undefined;
    return sortedMessages[sortedMessages.length - 1]!.id;
  }, [focusedRootId, sortedMessages]);

  useScrollMessageListToEnd(
    messagesEndRef,
    focusedRootId ? displayMessages : [],
  );

  const { setValue, getValue } = useEditDocument();
  const {
    promptText,
    setPromptText,
    clear,
    composerRootRef,
    sessionComposerModel,
    setSessionComposerModel,
    composerModelOptions,
  } = useDocumentChatComposer();
  const {
    handleSubmit,
    disabled: aiSubmitDisabled,
    processingPrompt,
  } = usePromptSubmit({
    documentId,
    isNewDocument,
    focusBlockId: undefined,
    onValueUpdate: setValue,
    getEditorSnapshot: () => stringifyEditorJs(getValue()),
    model: sessionComposerModel,
    threadRootId: messageLogThreadRootId,
    refetchMessages,
  });

  const onSubmitAiEdit = useCallback(async () => {
    if (!hasComposerContent(promptText)) return;
    const wire = promptText.trim();
    if (!wire) return;
    const ok = await handleSubmit(wire);
    if (ok) {
      clear();
    }
  }, [promptText, clear, handleSubmit]);

  const threadTrailing = messagesFeatureActive ? (
    <DocumentThreadPicker
      roots={sortedMessages}
      focusedRootId={focusedRootId}
      onSelectRoot={expandThread}
      onClearThreadFocus={replaceStripFocusParam}
      disabled={processingPrompt}
    />
  ) : null;

  const placeholder = messagesFeatureActive
    ? focusedRootId
      ? 'Describe how to change the document…'
      : 'e.g., Summarize this section, tighten the intro, fix tone…'
    : isNewDocument
      ? 'Save the document first to use AI modifications'
      : 'Describe the change…';

  const footerContent = (
    <VStack gap={2} align="stretch" width="100%">
      {!documentId && (
        <Text fontSize="xs" color="fg.muted">
          Save the document to use document messages.
        </Text>
      )}
      {documentId && !user.email?.trim() && (
        <Text fontSize="xs" color="fg.muted">
          Set your email in Settings to use document messages.
        </Text>
      )}
      {messagesFeatureActive ? (
        <>
          {!focusedRootId && messagesError ? (
            <Text fontSize="xs" color="fg.error">
              Could not load document messages.
            </Text>
          ) : null}
          {focusedRootId ? (
            <>
              <Box
                maxH="min(35vh, 280px)"
                minH="100px"
                overflow="hidden"
                display="flex"
                flexDirection="column"
                width="100%"
              >
                <MessageList
                  messages={displayMessages}
                  loading={isMessagesInitialLoad}
                  error={messagesError ?? undefined}
                  emptyMessage="No messages for this document yet."
                  focusedRootId={focusedRootId}
                  onExpandThread={expandThread}
                  messagesEndRef={messagesEndRef}
                  onRunChatMessageQuery={handleRunChatMessageQuery}
                  chatMessageQueryLoadingId={chatMessageQueryLoadingId}
                  alignMessagesToBottom
                  onMessageDeleted={() => void refetchMessages()}
                />
              </Box>
              <HStack gap={2} align="center" fontSize="xs" color="fg.muted">
                <Text>Replying in thread</Text>
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() => collapseThread()}
                >
                  Cancel
                </Button>
              </HStack>
            </>
          ) : null}
        </>
      ) : null}
      <ChatComposerRow
        rootRef={composerRootRef}
        value={promptText}
        onChange={setPromptText}
        onSubmit={onSubmitAiEdit}
        placeholder={placeholder}
        disabled={aiSubmitDisabled}
        loading={processingPrompt}
        trailingActions={threadTrailing}
        modelPicker={
          <DocumentComposerModelPicker
            value={sessionComposerModel}
            onValueChange={setSessionComposerModel}
            options={composerModelOptions}
            disabled={aiSubmitDisabled}
          />
        }
      />
    </VStack>
  );

  if (!active || !portalTarget) return null;

  return createPortal(footerContent, portalTarget);
}
